import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Text, Heading, Input, Select, Button, SortableTableHeader, LoadingSpinner, Tooltip } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { supabase } from '../lib/supabase'
import { modulesService } from '../services/modules'
import { 
  ExclamationTriangleIcon, 
  XMarkIcon,
  QuestionMarkCircleIcon,
  PaperAirplaneIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

const WEBHOOK_INSERIR_CCT_SCGP = 'https://webhook.pasiai.com/webhook/InserirCCTSCGP'

interface CCTData {
  id: number
  cct_convencao_id: number | null
  resposta: any
  created_at: string
  cnpj_entidade_laboral: string
  cnpj_entidade_patronal: string
  status: string | null
  link_documento: string | null
  link_download: string | null
  vigencia_inicio: string | null
  vigencia_fim: string | null
  partes: string | null
  codigo: string | null
  data_importacao: string | null
  id_scgp: number | string | null
  clausulas_especificas: string | null
}

type SortKey = 'id' | 'cct_convencao_id' | 'created_at' | 'status' | 'id_scgp' | 'vigencia_inicio' | 'vigencia_fim' | 'codigo' | 'data_importacao' | 'cnpj_entidade_laboral' | 'cnpj_entidade_patronal' | 'resposta'

const CCTLista = () => {
  const { hasPermission: canInsert } = usePermission('ccts_lista', 'insert')
  const [cctsData, setCctsData] = useState<CCTData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const [filtroCNPJLaboral, setFiltroCNPJLaboral] = useState('')
  const [filtroCNPJPatronal, setFiltroCNPJPatronal] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroDataUltimaBusca, setFiltroDataUltimaBusca] = useState('')
  const [tipoFiltroDataUltimaBusca, setTipoFiltroDataUltimaBusca] = useState<'data' | 'mes' | 'ano' | 'hoje'>('data')
  const [operadorFiltroDataUltimaBusca, setOperadorFiltroDataUltimaBusca] = useState<'igual' | 'a_partir_de' | 'ate'>('igual')
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedCCT, setSelectedCCT] = useState<CCTData | null>(null)
  const [detailsModalTab, setDetailsModalTab] = useState<'dados' | 'resposta' | 'clausulas'>('dados')
  const [isEnviarScgpModalOpen, setIsEnviarScgpModalOpen] = useState(false)
  const [enviarScgpModalStatus, setEnviarScgpModalStatus] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle')
  const [enviarScgpModalError, setEnviarScgpModalError] = useState<string | null>(null)
  const [enviarScgpModalConvencaoId, setEnviarScgpModalConvencaoId] = useState<number | null>(null)
  const [enviarScgpModalIdBusca, setEnviarScgpModalIdBusca] = useState<number | null>(null)
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState<SortKey>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  // Título e descrição vindos do cadastro do módulo
  const [moduleTitle, setModuleTitle] = useState<string>('CCTs • Resultado da busca')
  const [moduleDescription, setModuleDescription] = useState<string>('Resultado de buscas de CCTs')

  useEffect(() => {
    const fetchModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('ccts_lista')
        if (result.success && result.data) {
          if (result.data.display_name) setModuleTitle(result.data.display_name)
          if (result.data.description) setModuleDescription(result.data.description)
        }
      } catch (err: any) {
        console.error('Erro ao buscar informações do módulo:', err?.message)
      }
    }
    fetchModuleInfo()
  }, [])

  const fetchCCTs = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent)
    try {
      if (silent) {
        setRefreshError(null)
        setIsRefreshing(true)
      } else {
        setLoading(true)
        setError(null)
      }
      const { data: cctBuscas, error: errorBuscas } = await supabase
        .from('cct_buscas')
        .select('*')
        .order('created_at', { ascending: false })

      if (errorBuscas) throw errorBuscas

      const { data: cctConvencoes, error: errorConvencoes } = await supabase
        .from('cct_convencoes')
        .select('*')

      if (errorConvencoes) throw errorConvencoes

      const processedData: CCTData[] = (cctBuscas || []).map((busca: any) => {
        const convencao = (cctConvencoes || []).find((conv: any) => conv.id === busca.cct_convencao_id)
        return {
          id: busca.id,
          cct_convencao_id: busca.cct_convencao_id ?? null,
          resposta: busca.resposta,
          created_at: busca.created_at,
          cnpj_entidade_laboral: convencao?.cnpj_entidade_laboral ?? '',
          cnpj_entidade_patronal: convencao?.cnpj_entidade_patronal ?? '',
          status: busca.status ?? null,
          link_documento: busca.link_documento ?? null,
          link_download: busca.link_download ?? null,
          vigencia_inicio: busca.vigencia_inicio ?? null,
          vigencia_fim: busca.vigencia_fim ?? null,
          partes: busca.partes ?? null,
          codigo: busca.codigo ?? null,
          data_importacao: busca.data_importacao ?? null,
          id_scgp: busca.id_scgp ?? null,
          clausulas_especificas: busca.clausulas_especificas ?? null
        }
      })
      setCctsData(processedData)
    } catch (err: any) {
      const msg = err?.message ?? 'Erro ao carregar'
      if (silent) {
        setRefreshError(msg)
      } else {
        setError(msg)
      }
    } finally {
      if (silent) {
        setIsRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchCCTs()
  }, [fetchCCTs])

  const statusNormalizado = (s: string | null) => {
    const u = (s || '').trim().toUpperCase()
    if (u === 'NÃO LOCALIZADA MTE' || u === 'NAO LOCALIZADA MTE' || u === 'NÃO LOCALIZADA' || u === 'NAO LOCALIZADA') return 'NÃO LOCALIZADA'
    if (u === 'IMPORTADO SCGP' || u === 'IMPORTADO') return 'IMPORTADO SCGP'
    if (u === 'DISPONÍVEL PARA IMPORTAR') return 'DISPONÍVEL PARA IMPORTAR'
    return u || ''
  }

  const cctsFiltrados = useMemo(() => {
    const filtered = cctsData.filter(item => {
      const matchCNPJLaboral = !filtroCNPJLaboral || (item.cnpj_entidade_laboral || '').toLowerCase().includes(filtroCNPJLaboral.toLowerCase())
      const matchCNPJPatronal = !filtroCNPJPatronal || (item.cnpj_entidade_patronal || '').toLowerCase().includes(filtroCNPJPatronal.toLowerCase())
      const norm = statusNormalizado(item.status)
      const matchStatus = !filtroStatus || norm === filtroStatus

      let matchDataUltimaBusca = true
      const dataBusca = item.created_at
      if (filtroDataUltimaBusca && dataBusca) {
        const raw = String(dataBusca).trim()
        const dataStr = raw.match(/^\d{4}-\d{2}-\d{2}(T|$)/) ? raw.slice(0, 10) : (() => {
          const d = new Date(dataBusca)
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        })()
        const filtroStr = filtroDataUltimaBusca.trim()
        const isIgual = operadorFiltroDataUltimaBusca === 'igual'
        const isAte = operadorFiltroDataUltimaBusca === 'ate'
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          matchDataUltimaBusca = isIgual
            ? dataStr === filtroStr
            : isAte
              ? dataStr <= filtroStr
              : dataStr >= filtroStr
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          const [ano, mes] = filtroStr.split('-')
          const anoItem = parseInt(dataStr.slice(0, 4))
          const mesItem = parseInt(dataStr.slice(5, 7))
          matchDataUltimaBusca = isIgual
            ? anoItem === parseInt(ano) && mesItem === parseInt(mes)
            : isAte
              ? anoItem < parseInt(ano) || (anoItem === parseInt(ano) && mesItem <= parseInt(mes))
              : anoItem > parseInt(ano) || (anoItem === parseInt(ano) && mesItem >= parseInt(mes))
        } else if (filtroStr.match(/^\d{4}$/)) {
          const anoItem = parseInt(dataStr.slice(0, 4))
          matchDataUltimaBusca = isIgual
            ? anoItem === parseInt(filtroStr)
            : isAte
              ? anoItem <= parseInt(filtroStr)
              : anoItem >= parseInt(filtroStr)
        }
      } else if (filtroDataUltimaBusca && !dataBusca) {
        matchDataUltimaBusca = false
      }

      return matchCNPJLaboral && matchCNPJPatronal && matchStatus && matchDataUltimaBusca
    })

    return filtered.sort((a, b) => {
      let aVal: any = sortBy === 'id_scgp' ? a.id_scgp : (a as any)[sortBy]
      let bVal: any = sortBy === 'id_scgp' ? b.id_scgp : (b as any)[sortBy]
      if (sortBy === 'created_at' || sortBy === 'vigencia_inicio' || sortBy === 'vigencia_fim' || sortBy === 'data_importacao') {
        aVal = aVal ? new Date(aVal).getTime() : 0
        bVal = bVal ? new Date(bVal).getTime() : 0
      } else if (sortBy === 'id' || sortBy === 'cct_convencao_id') {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      } else if (sortBy === 'id_scgp') {
        aVal = aVal != null ? Number(aVal) : NaN
        bVal = bVal != null ? Number(bVal) : NaN
      } else if (sortBy === 'resposta') {
        aVal = aVal ? JSON.stringify(aVal) : ''
        bVal = bVal ? JSON.stringify(bVal) : ''
      } else {
        aVal = String(aVal ?? '').toLowerCase()
        bVal = String(bVal ?? '').toLowerCase()
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [cctsData, filtroCNPJLaboral, filtroCNPJPatronal, filtroStatus, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca, sortBy, sortOrder])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroCNPJLaboral, filtroCNPJPatronal, filtroStatus, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca])

  // Calcular dados da paginação
  const totalPages = Math.ceil(cctsFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const cctsPaginados = cctsFiltrados.slice(startIndex, endIndex)

  const handleRowClick = (item: CCTData) => {
    setSelectedCCT(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedCCT(null)
    setDetailsModalTab('dados')
  }

  const handleEnviarParaScgp = (convencaoId: number, idBusca: number) => {
    if (!canInsert) return
    setEnviarScgpModalConvencaoId(convencaoId)
    setEnviarScgpModalIdBusca(idBusca)
    setEnviarScgpModalStatus('confirm')
    setEnviarScgpModalError(null)
    setIsEnviarScgpModalOpen(true)
  }

  const handleConfirmEnviarScgp = async () => {
    if (!canInsert) return
    const id = enviarScgpModalConvencaoId
    if (id == null) return
    setEnviarScgpModalStatus('loading')
    try {
      const body: { id: number; id_busca?: number } = { id }
      if (enviarScgpModalIdBusca != null) body.id_busca = enviarScgpModalIdBusca
      const res = await fetch(WEBHOOK_INSERIR_CCT_SCGP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!res.ok) throw new Error(`Erro ${res.status}`)
      setEnviarScgpModalStatus('success')
      fetchCCTs()
    } catch (err: any) {
      setEnviarScgpModalError(err?.message ?? 'Não foi possível enviar para o SCGP.')
      setEnviarScgpModalStatus('error')
    }
  }

  const handleCloseEnviarScgpModal = () => {
    setIsEnviarScgpModalOpen(false)
    setEnviarScgpModalStatus('idle')
    setEnviarScgpModalError(null)
    setEnviarScgpModalConvencaoId(null)
    setEnviarScgpModalIdBusca(null)
  }

  const handleCancelEnviarScgpModal = () => {
    setIsEnviarScgpModalOpen(false)
    setEnviarScgpModalStatus('idle')
    setEnviarScgpModalError(null)
    setEnviarScgpModalConvencaoId(null)
    setEnviarScgpModalIdBusca(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as SortKey)
      setSortOrder('asc')
    }
  }

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400">
              Carregando CCTs...
            </Text>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400">
              Erro ao carregar CCTs: {error}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="ccts_lista" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="h-full flex flex-col">
              {/* Header - título e descrição do cadastro do módulo */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      {moduleTitle}
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      {moduleDescription}
                    </Text>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Tooltip content="Recarregar buscas e convenções do banco sem sair da página">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fetchCCTs({ silent: true })}
                        disabled={isRefreshing}
                        className="inline-flex items-center gap-2"
                        aria-label="Atualizar dados da lista de CCTs"
                      >
                        <ArrowPathIcon
                          className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                          aria-hidden
                        />
                        Atualizar dados
                      </Button>
                    </Tooltip>
                    {refreshError && (
                      <Text size="xs" className="text-red-600 dark:text-red-400 max-w-[16rem] text-right">
                        {refreshError}
                      </Text>
                    )}
                  </div>
                </div>
              </div>

              {/* Topo Colorido - Valores */}
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.length}
                      </Text>
                      <Text size="sm" className="text-white">
                        Total de Buscas
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.filter(c => statusNormalizado(c.status) === 'NÃO LOCALIZADA').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        NÃO LOCALIZADA
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.filter(c => statusNormalizado(c.status) === 'IMPORTADO SCGP').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        IMPORTADO SCGP
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-400 to-blue-500 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.filter(c => statusNormalizado(c.status) === 'DISPONÍVEL PARA IMPORTAR').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        DISPONÍVEL PARA IMPORTAR
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      CNPJ Laboral
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite o CNPJ laboral..."
                      value={filtroCNPJLaboral}
                      onChange={(e) => setFiltroCNPJLaboral(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      CNPJ Patronal
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite o CNPJ patronal..."
                      value={filtroCNPJPatronal}
                      onChange={(e) => setFiltroCNPJPatronal(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </Text>
                    <Select
                      value={filtroStatus}
                      onChange={(e) => setFiltroStatus(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Todos os status</option>
                      <option value="NÃO LOCALIZADA">NÃO LOCALIZADA</option>
                      <option value="IMPORTADO SCGP">IMPORTADO SCGP</option>
                      <option value="DISPONÍVEL PARA IMPORTAR">DISPONÍVEL PARA IMPORTAR</option>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Data última busca MTE
                  </Text>
                  <div className="flex flex-nowrap gap-2 items-end flex-wrap">
                    <Select
                      value={operadorFiltroDataUltimaBusca}
                      onChange={(e) => setOperadorFiltroDataUltimaBusca(e.target.value as 'igual' | 'a_partir_de' | 'ate')}
                      className="w-32 shrink-0"
                    >
                      <option value="igual">Igual</option>
                      <option value="a_partir_de">A partir de</option>
                      <option value="ate">Até</option>
                    </Select>
                    <Select
                      value={tipoFiltroDataUltimaBusca}
                      onChange={(e) => {
                        const novo = e.target.value as 'data' | 'mes' | 'ano' | 'hoje'
                        setTipoFiltroDataUltimaBusca(novo)
                        if (novo === 'hoje') {
                          setFiltroDataUltimaBusca(new Date().toISOString().slice(0, 10))
                        } else {
                          setFiltroDataUltimaBusca('')
                        }
                      }}
                      className="w-32 shrink-0"
                    >
                      <option value="data">Data</option>
                      <option value="hoje">Hoje</option>
                      <option value="mes">Mês</option>
                      <option value="ano">Ano</option>
                    </Select>
                    {tipoFiltroDataUltimaBusca === 'data' && (
                      <Input
                        type="date"
                        value={filtroDataUltimaBusca}
                        onChange={(e) => setFiltroDataUltimaBusca(e.target.value)}
                        className="flex-1 min-w-0 max-w-xs"
                      />
                    )}
                    {tipoFiltroDataUltimaBusca === 'hoje' && (
                      <Input
                        type="date"
                        value={filtroDataUltimaBusca || new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setFiltroDataUltimaBusca(e.target.value)}
                        className="flex-1 min-w-0 max-w-xs"
                      />
                    )}
                    {tipoFiltroDataUltimaBusca === 'mes' && (
                      <Input
                        type="month"
                        value={filtroDataUltimaBusca}
                        onChange={(e) => setFiltroDataUltimaBusca(e.target.value)}
                        className="flex-1 min-w-0 max-w-xs"
                      />
                    )}
                    {tipoFiltroDataUltimaBusca === 'ano' && (
                      <Input
                        type="number"
                        min={1900}
                        max={2100}
                        placeholder="Ano (ex: 2024)"
                        value={filtroDataUltimaBusca || ''}
                        onChange={(e) => setFiltroDataUltimaBusca(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="w-24 shrink-0"
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFiltroCNPJLaboral('')
                        setFiltroCNPJPatronal('')
                        setFiltroStatus('')
                        setFiltroDataUltimaBusca('')
                        setTipoFiltroDataUltimaBusca('data')
                        setOperadorFiltroDataUltimaBusca('igual')
                      }}
                      className="shrink-0 ml-auto"
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>

              {/* Tabela */}
              <div className="flex-1 px-6 py-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Histórico de buscas no MTE
                    </Heading>
                  </div>
                  
                  <div className="flex-1 overflow-auto">
                    <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm text-center">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 text-xs text-gray-500 dark:text-gray-400 uppercase">
                        <tr>
                          <SortableTableHeader sortKey="created_at" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            Data
                          </SortableTableHeader>
                          <th className="px-2 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <span>STATUS</span>
                              <Tooltip content={
                                <div className="space-y-2 text-left" style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                  <div>
                                    <span className="font-semibold text-yellow-400">NÃO LOCALIZADA:</span>
                                    <span className="block mt-0.5">Busca realizada no Sistema do MTE e não foi encontrada a CCT.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-green-400">IMPORTADO SCGP:</span>
                                    <span className="block mt-0.5">CCT localizada no sistema do MTE e importada para o SCGP.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-blue-400">DISPONÍVEL PARA IMPORTAR:</span>
                                    <span className="block mt-0.5">CCT localizada no sistema do MTE e disponível para ser importada para o SCGP. Basta usar o botão Enviar para o SCGP.</span>
                                  </div>
                                </div>
                              } constrainWidth>
                                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                              </Tooltip>
                            </div>
                          </th>
                          <SortableTableHeader sortKey="id_scgp" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            ID SCGP
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="vigencia_inicio" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            Vigência início
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="vigencia_fim" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            Vigência fim
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="codigo" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            N° MTE
                          </SortableTableHeader>
                          <th className="px-2 py-3 text-center">Documento</th>
                          <th className="px-2 py-3 text-center">Download</th>
                          {canInsert && <th className="px-2 py-3 text-center w-20">Ações</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {cctsPaginados.map((item, index) => {
                          const s = (item.status || '').trim().toUpperCase()
                          const isNaoLocalizada = s === 'NÃO LOCALIZADA MTE' || s === 'NAO LOCALIZADA MTE' || s === 'NÃO LOCALIZADA' || s === 'NAO LOCALIZADA'
                          const isDisponivel = s === 'DISPONÍVEL PARA IMPORTAR'
                          const isImportado = s === 'IMPORTADO SCGP' || s === 'IMPORTADO'
                          const statusClass = isNaoLocalizada ? 'text-yellow-600 dark:text-yellow-400' : isDisponivel ? 'text-blue-600 dark:text-blue-400' : isImportado ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
                          const statusLabel = isNaoLocalizada ? 'NÃO LOCALIZADA' : (item.status ?? '—')
                          return (
                            <tr
                              key={item.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                              onClick={() => handleRowClick(item)}
                            >
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(item.created_at)}</td>
                              <td className="px-2 py-2 text-center">
                                <span className={`text-sm font-medium ${statusClass}`}>{statusLabel}</span>
                              </td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.id_scgp ?? '—'}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateShort(item.vigencia_inicio)}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateShort(item.vigencia_fim)}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300">{item.codigo ?? '—'}</td>
                              <td className="px-2 py-2 text-center">
                                {item.link_documento ? (
                                  <a href={item.link_documento} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-xs" onClick={e => e.stopPropagation()}>Abrir</a>
                                ) : '—'}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {item.link_download ? (
                                  <a href={item.link_download} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-xs" onClick={e => e.stopPropagation()}>Baixar</a>
                                ) : '—'}
                              </td>
                              {canInsert && (
                                <td className="px-2 py-2 text-center" onClick={e => e.stopPropagation()}>
                                  {isDisponivel && item.cct_convencao_id != null ? (
                                    <Tooltip content="Enviar para SCGP">
                                      <button
                                        type="button"
                                        disabled={isEnviarScgpModalOpen}
                                        onClick={() => handleEnviarParaScgp(item.cct_convencao_id!, item.id)}
                                        className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:text-green-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-gray-700 focus:outline-none transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                        aria-label="Enviar para SCGP"
                                      >
                                        <PaperAirplaneIcon className="h-5 w-5" />
                                      </button>
                                    </Tooltip>
                                  ) : (
                                    <span className="text-gray-400 dark:text-gray-500">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Controles de Paginação */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                          <Text size="sm">
                            Mostrando {startIndex + 1} a {Math.min(endIndex, cctsFiltrados.length)} de {cctsFiltrados.length} registros
                          </Text>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Anterior
                          </button>
                          
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNumber: number;
                              if (totalPages <= 5) {
                                pageNumber = i + 1;
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i;
                              } else {
                                pageNumber = currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    currentPage === pageNumber
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            })}
                          </div>
                          
                          <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Próximo
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </PermissionGuard>
      </div>

      {/* Modal Enviar para SCGP */}
      {isEnviarScgpModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="flex flex-col items-center gap-4 py-4">
                {enviarScgpModalStatus === 'confirm' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <QuestionMarkCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Deseja confirmar o envio desta convenção para o SCGP?
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'loading' && (
                  <>
                    <LoadingSpinner size="sm" className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Enviando para o SCGP...
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'success' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Enviado com sucesso. Feche para atualizar os dados.
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'error' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      {enviarScgpModalError ?? 'Erro ao enviar para o SCGP.'}
                    </Text>
                  </>
                )}
              </div>
              <div className="mt-4 flex justify-center gap-2">
                {enviarScgpModalStatus === 'confirm' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEnviarScgpModal}>
                      Cancelar
                    </Button>
                    <Button variant="solid" size="sm" onClick={handleConfirmEnviarScgp}>
                      Confirmar
                    </Button>
                  </>
                )}
                {(enviarScgpModalStatus === 'loading' || enviarScgpModalStatus === 'success' || enviarScgpModalStatus === 'error') && (
                  <Button variant="solid" size="sm" onClick={handleCloseEnviarScgpModal}>
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes - Registro do Histórico de buscas no MTE */}
      {isDetailsModalOpen && selectedCCT && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseDetailsModal} />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Registro do Histórico de buscas no MTE
                </Heading>
                <button
                  type="button"
                  onClick={handleCloseDetailsModal}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none dark:hover:text-gray-300"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
                <button
                  type="button"
                  onClick={() => setDetailsModalTab('dados')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailsModalTab === 'dados' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Dados
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsModalTab('resposta')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailsModalTab === 'resposta' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Resposta
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsModalTab('clausulas')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailsModalTab === 'clausulas' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Cláusulas específicas
                </button>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {detailsModalTab === 'dados' ? (
                  <div className="space-y-3 text-sm">
                    {[
                      { label: 'ID', value: selectedCCT.id },
                      { label: 'Data', value: formatDate(selectedCCT.created_at) },
                      { label: 'CCT Convenção ID', value: selectedCCT.cct_convencao_id != null ? String(selectedCCT.cct_convencao_id) : '—' },
                      { label: 'Status', value: statusNormalizado(selectedCCT.status) || '—' },
                      { label: 'Link documento', value: selectedCCT.link_documento ? (
                        <a href={selectedCCT.link_documento} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline" onClick={e => e.stopPropagation()}>Abrir</a>
                      ) : '—' },
                      { label: 'Link download', value: selectedCCT.link_download ? (
                        <a href={selectedCCT.link_download} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline" onClick={e => e.stopPropagation()}>Baixar</a>
                      ) : '—' },
                      { label: 'Vigência início', value: formatDateShort(selectedCCT.vigencia_inicio) },
                      { label: 'Vigência fim', value: formatDateShort(selectedCCT.vigencia_fim) },
                      { label: 'Partes', value: selectedCCT.partes ?? '—' },
                      { label: 'N° MTE', value: selectedCCT.codigo ?? '—' },
                      { label: 'Data importação', value: selectedCCT.data_importacao ? formatDateShort(selectedCCT.data_importacao) : '—' },
                      { label: 'ID SCGP', value: selectedCCT.id_scgp != null ? String(selectedCCT.id_scgp) : '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
                        <span className="text-gray-700 dark:text-gray-300 break-words">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : detailsModalTab === 'resposta' ? (
                  <div className="text-sm">
                    {(() => {
                      const v = selectedCCT.resposta
                      const str = v == null ? '' : String(v).trim()
                      const empty = str === '' || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined'
                      return empty ? (
                        <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                      ) : (
                        <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{v}</pre>
                      )
                    })()}
                  </div>
                ) : (
                  <div className="text-sm">
                    {(() => {
                      const v = selectedCCT.clausulas_especificas
                      const str = v == null ? '' : String(v).trim()
                      const empty = str === '' || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined'
                      return empty ? (
                        <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                      ) : (
                        <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{v}</pre>
                      )
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CCTLista